import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Car, CheckCircle2, Clock, FileText, Loader2, Mail, MapPin, Users } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n";

export default function Confirmation() {
  const { bookingId } = useParams();
  const { t } = useLang();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch(() => setError(true));
  }, [bookingId]);

  return (
    <div className="flex min-h-screen flex-col bg-sand" data-testid="confirmation-page">
      <Navbar />
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-24 pt-36 sm:px-6">
        {!booking && !error && (
          <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-azure" /></div>
        )}
        {error && (
          <div className="rounded-3xl bg-white p-12 text-center shadow-xl" data-testid="confirmation-error">
            <h1 className="font-serif text-2xl font-semibold text-navy">{t("confirmation.notfound_title")}</h1>
            <p className="mt-3 text-sm text-navy/60">{t("confirmation.notfound_text")}</p>
          </div>
        )}
        {booking && (
          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl" data-testid="confirmation-card">
            <div className="bg-navy px-8 py-10 text-center sm:px-12">
              <CheckCircle2 size={52} className="mx-auto text-emerald-400" />
              <h1 className="mt-5 font-serif text-3xl font-semibold text-white" data-testid="confirmation-title">
                {booking.status === "pending_payment" ? t("confirmation.pending") : t("confirmation.confirmed")}
              </h1>
              <p className="mt-2 text-sm text-white/60">
                {t("confirmation.reference")}{" "}
                <span className="font-mono font-bold text-gold" data-testid="confirmation-ref">{booking.id.slice(0, 8).toUpperCase()}</span>
              </p>
            </div>

            <div className="p-8 sm:p-10">
              <dl className="space-y-4 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <dt className="flex items-center gap-2 text-navy/50"><MapPin size={15} className="text-azure" /> {t("confirmation.r_pickup")}</dt>
                  <dd className="text-right font-semibold text-navy">
                    {booking.pickup_label}
                    {booking.pickup_address && <span className="block text-xs font-medium text-navy/50" data-testid="confirmation-pickup-address">{booking.pickup_address}</span>}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="flex items-center gap-2 text-navy/50"><MapPin size={15} className="text-gold" /> {t("confirmation.r_dropoff")}</dt>
                  <dd className="text-right font-semibold text-navy">
                    {booking.dropoff_label}
                    {booking.dropoff_address && <span className="block text-xs font-medium text-navy/50" data-testid="confirmation-dropoff-address">{booking.dropoff_address}</span>}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-navy/50"><Calendar size={15} className="text-azure" /> {t("confirmation.r_date")}</dt>
                  <dd className="font-semibold text-navy">{booking.date}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-navy/50"><Clock size={15} className="text-azure" /> {t("confirmation.r_time")}</dt>
                  <dd className="font-semibold text-navy">{booking.time}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-navy/50"><Car size={15} className="text-azure" /> {t("confirmation.r_vehicle")}</dt>
                  <dd className="font-semibold text-navy">{booking.vehicle === "berline" ? "Berline Executive" : "Van Premium"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-navy/50"><Users size={15} className="text-azure" /> {t("confirmation.r_passengers")}</dt>
                  <dd className="font-semibold text-navy">{booking.passengers} · {booking.luggage}</dd>
                </div>
                {booking.voucher_no && (
                  <div className="flex items-center justify-between">
                    <dt className="flex items-center gap-2 text-navy/50"><FileText size={15} className="text-azure" /> {t("confirmation.r_voucher")}</dt>
                    <dd className="font-mono font-bold text-gold" data-testid="confirmation-voucher-no">{booking.voucher_no}</dd>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                  <dt className="font-bold text-navy">{t("confirmation.r_fare")}</dt>
                  <dd className="font-serif text-3xl font-semibold text-azure" data-testid="confirmation-price">{booking.price_eur} €</dd>
                </div>
              </dl>

              <div className="mt-8 rounded-2xl bg-azure-soft p-5 text-sm leading-relaxed text-navy/70">
                <p className="flex items-start gap-2">
                  <Mail size={15} className="mt-0.5 shrink-0 text-azure" />
                  {booking.payment_method === "online"
                    ? t("confirmation.email_online", { email: booking.email })
                    : booking.payment_method === "onboard_cash"
                      ? t("confirmation.email_onboard_cash", { email: booking.email, price: booking.price_eur })
                      : t("confirmation.email_onboard_card", { email: booking.email, price: booking.price_eur })}
                </p>
              </div>

              {booking.voucher_token && (
                <Link
                  to={`/bon-de-transport/${booking.voucher_token}`}
                  data-testid="confirmation-voucher-link"
                  className="mt-6 flex items-center justify-center gap-2 rounded-full border-2 border-navy py-3.5 text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white"
                >
                  <FileText size={16} /> {t("confirmation.voucher_link")}
                </Link>
              )}

              <Link to="/" data-testid="confirmation-home-button"
                className="mt-4 block rounded-full bg-azure py-4 text-center text-sm font-bold text-white transition-colors hover:bg-azure-hover">
                {t("common.back_home")}
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
